import { db, makeId } from '../lib/db'
import type { Bookmark, DownloadRecord, Highlight, MedicalLibraryArticle, Note, Progress, QuizAttempt, QuizSession, SessionAnswer, SessionSnapshot, User } from '../types'
import { syncService } from './sync-service'
import { firebaseQbankSync } from './firebase-qbank-sync'

const now = () => new Date().toISOString()
const queue = (userId: string) => syncService.queue(userId)

export const repository = {
  subjects: () => db.subjects.orderBy('name').toArray(),
  subject: (id: string) => db.subjects.get(id),
  lessons: (subjectId?: string) => subjectId ? db.lessons.where('subjectId').equals(subjectId).sortBy('order') : db.lessons.toArray(),
  lesson: (id: string) => db.lessons.get(id),
  async libraryArticles(query = ''): Promise<MedicalLibraryArticle[]> {
    const normalized = query.trim().toLowerCase()
    const articles = await db.libraryArticles.toArray()
    return articles
      .filter((article) => !normalized || `${article.title} ${article.summary} ${article.system} ${article.tags.join(' ')} ${article.content}`.toLowerCase().includes(normalized))
      .sort((a, b) => a.system.localeCompare(b.system) || a.order - b.order || a.title.localeCompare(b.title))
  },
  libraryArticle: (id: string) => db.libraryArticles.get(id),
  async librarySystems() {
    const articles = await db.libraryArticles.toArray()
    return [...new Set(articles.map((article) => article.system))].sort((a, b) => a.localeCompare(b))
  },
  questions: (subjectId?: string) => subjectId ? db.questions.where('subjectId').equals(subjectId).toArray() : db.questions.toArray(),
  questionBanks: () => db.questionBanks.orderBy('name').filter((bank) => bank.installed).toArray(),
  clinicalCases: () => db.clinicalCases.toArray(),
  async saveAttempt(input: Omit<QuizAttempt, 'id' | 'createdAt'>) { const attempt: QuizAttempt = { ...input, id: makeId('attempt'), createdAt: now() }; await db.attempts.add(attempt); firebaseQbankSync.mark(input.userId, 'attempts', attempt.id); return attempt },
  attempts: (userId: string) => db.attempts.where('userId').equals(userId).toArray(),
  quizSessions: (userId: string, bankId?: string) => {
    const query = db.quizSessions.where('userId').equals(userId)
    return (bankId ? query.filter((session) => session.bankId === bankId) : query).reverse().sortBy('createdAt')
  },
  quizSession: (id: string) => db.quizSessions.get(id),
  async saveQuizSession(input: Omit<QuizSession, 'id' | 'createdAt'>) {
    const session: QuizSession = { ...input, id: makeId('session'), createdAt: now() }
    await db.quizSessions.add(session)
    firebaseQbankSync.mark(input.userId, 'sessions', session.id)
    return session
  },
  async updateQuizSession(id: string, patch: Partial<QuizSession>) {
    const current = await db.quizSessions.get(id)
    if (!current) return undefined
    const next = { ...current, ...patch }
    await db.quizSessions.put(next)
    firebaseQbankSync.mark(current.userId, 'sessions', next.id)
    return next
  },
  async completeQuizSession(id: string, _userId: string, elapsedSeconds?: number) {
    return repository.updateQuizSession(id, { status: 'completed', completedAt: now(), ...(elapsedSeconds == null ? {} : { elapsedSeconds }) })
  },
  async deleteQuizSession(id: string) {
    const current = await db.quizSessions.get(id)
    if (!current) return false
    const answers = await db.sessionAnswers.where('sessionId').equals(id).toArray()
    await db.transaction('rw', [db.quizSessions, db.sessionSnapshots, db.sessionAnswers], async () => {
      await db.quizSessions.delete(id)
      await db.sessionSnapshots.delete(id)
      await db.sessionAnswers.where('sessionId').equals(id).delete()
    })
    await firebaseQbankSync.remove(current.userId, 'sessions', current.id)
    await firebaseQbankSync.remove(current.userId, 'snapshots', current.id)
    await Promise.all(answers.map((answer) => firebaseQbankSync.remove(current.userId, 'answers', `${answer.sessionId}:${answer.questionId}`)))
    return true
  },
  async deleteQuizSessions(userId: string, bankId: string) {
    const sessions = (await db.quizSessions.where('userId').equals(userId).toArray())
      .filter((session) => session.bankId === bankId || session.packageId === bankId)
    const sessionIds = sessions.map((session) => session.id)
    if (!sessionIds.length) return 0
    const answers = await db.sessionAnswers.where('sessionId').anyOf(sessionIds).toArray()
    await db.transaction('rw', [db.quizSessions, db.sessionSnapshots, db.sessionAnswers], async () => {
      await db.quizSessions.bulkDelete(sessionIds)
      await db.sessionSnapshots.bulkDelete(sessionIds)
      await db.sessionAnswers.where('sessionId').anyOf(sessionIds).delete()
    })
    await Promise.all(sessions.flatMap((session) => [
      firebaseQbankSync.remove(userId, 'sessions', session.id),
      firebaseQbankSync.remove(userId, 'snapshots', session.id),
    ]).concat(answers.map((answer) => firebaseQbankSync.remove(userId, 'answers', `${answer.sessionId}:${answer.questionId}`))))
    return sessionIds.length
  },
  async resetQbankProgress(userId: string, bankId: string) {
    const questionPrefix = `external:${bankId}:`
    const [attempts, bookmarks, sessions] = await Promise.all([
      db.attempts.where('userId').equals(userId).toArray(),
      db.bookmarks.where('userId').equals(userId).toArray(),
      db.quizSessions.where('userId').equals(userId).toArray(),
    ])
    const matchingAttempts = attempts.filter((item) => item.bankId === bankId || item.packageId === bankId || item.questionId.startsWith(questionPrefix))
    const matchingBookmarks = bookmarks.filter((item) => item.entityType === 'question' && item.entityId.startsWith(questionPrefix))
    const matchingSessions = sessions.filter((item) => item.bankId === bankId || item.packageId === bankId)
    const sessionIds = matchingSessions.map((item) => item.id)
    const matchingAnswers = sessionIds.length ? await db.sessionAnswers.where('sessionId').anyOf(sessionIds).toArray() : []

    await db.transaction('rw', [db.attempts, db.bookmarks, db.quizSessions, db.sessionSnapshots, db.sessionAnswers, db.syncTombstones], async () => {
      await db.attempts.bulkDelete(matchingAttempts.map((item) => item.id))
      await db.bookmarks.bulkDelete(matchingBookmarks.map((item) => item.id))
      if (matchingBookmarks.length) {
        await db.syncTombstones.bulkPut(matchingBookmarks.map((item) => ({ id: item.id, userId, table: 'bookmarks' as const, createdAt: now() })))
      }
      await db.quizSessions.bulkDelete(sessionIds)
      await db.sessionSnapshots.bulkDelete(sessionIds)
      if (sessionIds.length) await db.sessionAnswers.where('sessionId').anyOf(sessionIds).delete()
    })
    await Promise.all([
      ...matchingAttempts.map((item) => firebaseQbankSync.remove(userId, 'attempts', item.id)),
      ...matchingBookmarks.map((item) => firebaseQbankSync.remove(userId, 'bookmarks', item.id)),
      ...matchingSessions.flatMap((item) => [firebaseQbankSync.remove(userId, 'sessions', item.id), firebaseQbankSync.remove(userId, 'snapshots', item.id)]),
      ...matchingAnswers.map((item) => firebaseQbankSync.remove(userId, 'answers', `${item.sessionId}:${item.questionId}`)),
    ])
    return {
      attempts: matchingAttempts.length,
      bookmarks: matchingBookmarks.length,
      sessions: matchingSessions.length,
    }
  },
  async saveProgress(input: Omit<Progress, 'id' | 'updatedAt'>) { const existing = await db.progress.where('[userId+entityId]').equals([input.userId, input.entityId]).first(); const progress: Progress = { ...input, id: existing?.id ?? makeId('progress'), updatedAt: now() }; await db.progress.put(progress); queue(input.userId); return progress },
  progress: (userId: string) => db.progress.where('userId').equals(userId).toArray(),
  notes: (userId: string) => db.notes.where('userId').equals(userId).filter((note) => !note.deletedAt).reverse().sortBy('updatedAt'),
  async saveNote(input: Partial<Note> & Pick<Note, 'userId' | 'title' | 'content' | 'contextType'>) { const existing = input.id ? await db.notes.get(input.id) : undefined; const note: Note = { id: existing?.id ?? makeId('note'), userId: input.userId, title: input.title, content: input.content, tags: input.tags ?? existing?.tags ?? [], contextType: input.contextType, contextId: input.contextId, source: input.source ?? existing?.source, parentId: input.parentId, createdAt: existing?.createdAt ?? now(), updatedAt: now() }; await db.notes.put(note); queue(input.userId); return note },
  async deleteNote(id: string) { const note = await db.notes.get(id); if (note) { await db.notes.put({ ...note, deletedAt: now(), updatedAt: now() }); queue(note.userId) } },
  highlights: (userId: string, lessonId: string) => db.highlights.where('[userId+lessonId]').equals([userId, lessonId]).toArray(),
  async saveHighlight(input: Omit<Highlight, 'id' | 'createdAt' | 'updatedAt'>) { const highlight: Highlight = { ...input, id: makeId('highlight'), createdAt: now(), updatedAt: now() }; await db.highlights.add(highlight); queue(input.userId); return highlight },
  async deleteHighlight(id: string) { const item = await db.highlights.get(id); if (item) { await db.transaction('rw', [db.highlights, db.syncTombstones], async () => { await db.highlights.delete(id); await db.syncTombstones.put({ id, userId: item.userId, table: 'highlights', createdAt: now() }) }); queue(item.userId) } },
  bookmarks: (userId: string) => db.bookmarks.where('userId').equals(userId).reverse().sortBy('createdAt'),
  async toggleBookmark(input: Omit<Bookmark, 'id' | 'createdAt'>) { const existing = await db.bookmarks.where('[userId+entityId]').equals([input.userId, input.entityId]).first(); if (existing) { await db.transaction('rw', [db.bookmarks, db.syncTombstones], async () => { await db.bookmarks.delete(existing.id); await db.syncTombstones.put({ id: existing.id, userId: input.userId, table: 'bookmarks', createdAt: now() }) }); if (input.entityType === 'question') await firebaseQbankSync.remove(input.userId, 'bookmarks', existing.id); else queue(input.userId); return false } const bookmark = { ...input, id: makeId('bookmark'), createdAt: now() }; await db.bookmarks.add(bookmark); if (input.entityType === 'question') firebaseQbankSync.mark(input.userId, 'bookmarks', bookmark.id); else queue(input.userId); return true },
  async setQuestionBookmark(input: Omit<Bookmark, 'id' | 'createdAt'>, active: boolean) {
    const existing = await db.bookmarks.where('[userId+entityId]').equals([input.userId, input.entityId]).first()
    if (active && !existing) await db.bookmarks.add({ ...input, id: makeId('bookmark'), createdAt: now() })
    if (!active && existing) {
      await db.transaction('rw', [db.bookmarks, db.syncTombstones], async () => {
        await db.bookmarks.delete(existing.id)
        await db.syncTombstones.put({ id: existing.id, userId: input.userId, table: 'bookmarks', createdAt: now() })
      })
    }
    if (Boolean(existing) !== active) {
      if (!active && existing) await firebaseQbankSync.remove(input.userId, 'bookmarks', existing.id)
      else if (active) {
        const bookmark = await db.bookmarks.where('[userId+entityId]').equals([input.userId, input.entityId]).first()
        if (bookmark) firebaseQbankSync.mark(input.userId, 'bookmarks', bookmark.id)
      }
    }
    return active
  },
  flashcards: (userId: string) => db.flashcards.where('userId').equals(userId).toArray(),
  async saveFlashcard(userId: string, subjectId: string, front: string, back: string) { const card = { id: makeId('card'), userId, subjectId, front, back, interval: 1, repetitions: 0, ease: 2.5, dueAt: now(), createdAt: now() }; await db.flashcards.add(card); queue(userId); return card },
  notifications: (userId: string) => db.notifications.where('userId').equals(userId).reverse().sortBy('createdAt'),
  markNotificationRead: async (id: string) => { await db.notifications.update(id, { read: true }) },
  communityPosts: () => db.community.reverse().sortBy('createdAt'),
  async createPost(user: User, title: string, body: string, topic: string) { const post = { id: makeId('post'), userId: user.id, author: user.name, title, body, topic, likes: 0, createdAt: now() }; await db.community.add(post); queue(user.id); return post },
  communityReplies: (postId: string) => db.communityReplies.where('postId').equals(postId).sortBy('createdAt'),
  async createReply(user: User, postId: string, body: string) { const reply = { id: makeId('reply'), postId, userId: user.id, author: user.name, body, createdAt: now() }; await db.communityReplies.add(reply); queue(user.id); return reply },
  async reportQuestion(userId: string, questionId: string) { const key = `question-report:${makeId('report')}`; await db.settings.put({ key, value: { userId, questionId, status: 'queued' }, updatedAt: now() }); return key },
  downloads: (userId: string) => db.downloads.where('userId').equals(userId).toArray(),
  async saveDownload(input: Omit<DownloadRecord, 'id' | 'updatedAt'>) { const existing = await db.downloads.where('[userId+resourceId]').equals([input.userId, input.resourceId]).first(); const record: DownloadRecord = { ...input, id: existing?.id ?? makeId('download'), updatedAt: now() }; await db.downloads.put(record); return record },

  /* ── Session Persistence (v7) ────────────────────────────────────────── */

  /** Save or update a full session snapshot for crash-safe resume. */
  async saveSessionSnapshot(snapshot: SessionSnapshot) {
    await db.sessionSnapshots.put({ ...snapshot, updatedAt: now() })
    const session = await db.quizSessions.get(snapshot.sessionId)
    if (session) firebaseQbankSync.mark(session.userId, 'snapshots', snapshot.sessionId)
  },

  /** Retrieve a session snapshot by sessionId, or undefined if none exists. */
  async getSessionSnapshot(sessionId: string): Promise<SessionSnapshot | undefined> {
    return db.sessionSnapshots.get(sessionId)
  },

  /** Delete a session snapshot after session completion. */
  async deleteSessionSnapshot(sessionId: string) {
    await db.sessionSnapshots.delete(sessionId)
    const session = await db.quizSessions.get(sessionId)
    if (session) await firebaseQbankSync.remove(session.userId, 'snapshots', sessionId)
  },

  /** Save or update a single answer within a session. */
  async saveSessionAnswer(answer: SessionAnswer) {
    const current = await db.sessionAnswers.get([answer.sessionId, answer.questionId])
    await db.sessionAnswers.put({
      ...current,
      ...answer,
      submitted: Boolean(current?.submitted || answer.submitted),
      submittedAt: answer.submittedAt || current?.submittedAt || null,
    })
    const session = await db.quizSessions.get(answer.sessionId)
    if (session) firebaseQbankSync.mark(session.userId, 'answers', `${answer.sessionId}:${answer.questionId}`)
  },

  /** Bulk-save all answers for a session (used during initial creation or batch scoring). */
  async saveSessionAnswersBulk(answers: SessionAnswer[]) {
    await db.sessionAnswers.bulkPut(answers)
    const session = answers[0] ? await db.quizSessions.get(answers[0].sessionId) : undefined
    if (session) answers.forEach((answer) => firebaseQbankSync.mark(session.userId, 'answers', `${answer.sessionId}:${answer.questionId}`))
  },

  /** Get all answers for a given session. */
  async getSessionAnswers(sessionId: string): Promise<SessionAnswer[]> {
    return db.sessionAnswers.where('sessionId').equals(sessionId).toArray()
  },

  /** Delete all answers and snapshot for a session (cleanup). */
  async clearSessionData(sessionId: string) {
    const answers = await db.sessionAnswers.where('sessionId').equals(sessionId).toArray()
    await db.transaction('rw', [db.sessionSnapshots, db.sessionAnswers], async () => {
      await db.sessionSnapshots.delete(sessionId)
      await db.sessionAnswers.where('sessionId').equals(sessionId).delete()
    })
    const session = await db.quizSessions.get(sessionId)
    if (session) {
      await firebaseQbankSync.remove(session.userId, 'snapshots', sessionId)
      await Promise.all(answers.map((answer) => firebaseQbankSync.remove(session.userId, 'answers', `${answer.sessionId}:${answer.questionId}`)))
    }
  },
}
